import React, { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Box, Button, Paper, Typography, Stack, TextField, IconButton, Tooltip, List, ListItem, ListItemText } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

function UUIDGenerator() {
  const [uuids, setUuids] = useState([]);
  const [count, setCount] = useState(5);

  const generateUuids = useCallback(() => {
    const newUuids = Array.from({ length: count }, () => uuidv4());
    setUuids(prev => [...newUuids, ...prev]);
  }, [count]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <>
      <SEO
        title="UUID/GUID Generator | Bulk v4 UUIDs | UmbrellaCo"
        description="Generate multiple random version 4 UUIDs (Universally Unique Identifiers) instantly. Copy to clipboard for use in databases and code."
        keywords="uuid generator, guid generator, random uuid, bulk uuid, v4 uuid"
        url="/dev-tools/uuid-generator"
        jsonLd={createSoftwareAppSchema(
          'UUID Generator',
          'Generate bulk random v4 UUIDs instantly in your browser.',
          'https://umbrellaco.in/dev-tools/uuid-generator',
          ['UUID Generation', 'Developer Tools', 'Random Identifiers']
        )}
      />
      <ToolPageContainer
        title="UUID Generator"
        description="Generate highly secure, universally unique identifiers (v4 UUIDs) in bulk for use as database keys or temporary tokens."
      >

      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Amount to Generate"
          type="number"
          value={count}
          onChange={(e) => setCount(Math.max(1, parseInt(e.target.value, 10) || 1))}
          size="small"
          inputProps={{ min: 1, max: 100 }}
        />
        <Button
          variant="contained"
          onClick={generateUuids}
          startIcon={<AddIcon />}
        >
          Generate
        </Button>
        <Button
          variant="outlined"
          onClick={() => setUuids([])}
          startIcon={<ClearAllIcon />}
          disabled={uuids.length === 0}
        >
          Clear
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ flexGrow: 1, overflowY: 'auto' }}>
        <List dense>
          {uuids.map((uuid) => (
            <ListItem
              key={uuid}
              secondaryAction={
                <Tooltip title="Copy">
                  <IconButton edge="end" onClick={() => handleCopy(uuid)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            >
              <ListItemText primary={uuid} primaryTypographyProps={{ fontFamily: 'monospace' }} />
            </ListItem>
          ))}
        </List>
      </Paper>
      </ToolPageContainer>
    </>
  );
}

export default UUIDGenerator;
